import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api } from '../api';

export type HintTexts = Record<string, { hint: string; manualAnchor: string }>;

export const HINTS_KEY = ['hints', 'texts'] as const;
export const HINTS_SETTING_KEY = ['hints', 'setting'] as const;
export const MANUAL_KEY = ['hints', 'manual'] as const;

type HintsContextValue = {
  /** Texts by featureId; empty until loaded. */
  texts: HintTexts;
  /** The user's own switch (default off). */
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** True while the first load of the setting is running (the switch is not shown wrongly). */
  loading: boolean;
};

const HintsContext = createContext<HintsContextValue>({ texts: {}, enabled: false, setEnabled: () => {}, loading: false });

export const useHints = () => useContext(HintsContext);

/** Loads the hint texts and the user's setting once (TanStack Query cache) for the logged-in area. */
export function HintsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const setting = useQuery({
    queryKey: HINTS_SETTING_KEY,
    queryFn: () => api<{ enabled: boolean }>('/api/me/settings/hints'),
    staleTime: Infinity,
  });
  const texts = useQuery({ queryKey: HINTS_KEY, queryFn: () => api<HintTexts>('/api/hints'),
    staleTime: 60_000, // owner edits reach everyone within a minute of returning to the app
    refetchOnWindowFocus: true,
  });

  const save = useMutation({
    mutationFn: (enabled: boolean) => api<{ enabled: boolean }>('/api/me/settings/hints', { method: 'PUT', body: { enabled } }),
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: HINTS_SETTING_KEY });
      const previous = queryClient.getQueryData<{ enabled: boolean }>(HINTS_SETTING_KEY);
      queryClient.setQueryData(HINTS_SETTING_KEY, { enabled });
      return { previous };
    },
    onError: (_error, _enabled, context) => {
      if (context?.previous) queryClient.setQueryData(HINTS_SETTING_KEY, context.previous);
    },
  });

  const value = useMemo<HintsContextValue>(
    () => ({
      texts: texts.data ?? {},
      enabled: setting.data?.enabled ?? false,
      setEnabled: (enabled) => save.mutate(enabled),
      loading: setting.isLoading,
    }),
    [texts.data, setting.data, setting.isLoading, save],
  );
  return <HintsContext.Provider value={value}>{children}</HintsContext.Provider>;
}
